<template>
  <div class="summary">
    <div class="scroll-container" ref="scrollContainer">
      <div class="container-fluid">
        <header class="summary__header">
          <h2 class="header__headline">{{ $t('pages.bookingSummary.title') }}</h2>
          <div class="header__close" @click="toggleBookingDetails">
            <iconCloseDark></iconCloseDark>
          </div>
          <div class="row">
            <div class="col-4">
              <div class="header__image-wrapper">
                <div class="header__image">
                  <img class="image img-fluid" alt="image" :src="urls.image"/>
                </div>
              </div>
            </div>
            <div class="col-12 col-sm-8">
              <div class="header__info">
                <div>
                  <h3>{{ campground.name }}</h3>
                  <div class="row">
                    <div class="col-12">
                      <ul class="address-list">
                        <li v-if="campground.street1">{{ campground.street1 }}</li>
                        <li v-if="campground.street2">{{ campground.street2 }}</li>
                        <li v-if="campground.city">{{ campground.zip_code }} {{
                            campground.city
                          }}
                        </li>
                        <li v-if="campground.country">{{
                            getCountryByCode(campground.country)
                          }}
                        </li>
                      </ul>

                      <br>
                      <h4
                        v-if="attributes && attributes.length > 0"
                        class="d-lg-none"
                      >
                        {{ $t('generic.labels.attributes') }}
                        <button
                          class="info-icon"
                          @click.prevent.stop="$modal.show(campground.name)"
                        >
                          <iconInfoBlack/>
                        </button>
                      </h4>

                      <modal
                        :name="campground.name"
                        height="auto"
                        adaptive
                        scrollable
                      >
                        <div class="attribute-modal">
                          <button
                            class="attribute-modal__close"
                            @click="$modal.hide(campground.name)"
                          >
                            <icon-close-dark/>
                          </button>

                          <h3 class="meta__headline">
                            {{ campground.name }}
                          </h3>

                          <div class="row">
                            <template v-for="category in attributes">
                              <div
                                class="col-12 col-md-6"
                                :key="category.name"
                              >
                                <h4>{{ category.name }}</h4>
                                <ul class="attribute-list">
                                  <li
                                    v-for="attribute in category.attributes"
                                    :key="attribute.name"
                                  >
                                    <icon-checkmark-white
                                      v-if="attribute.positive_flag"
                                      class="attribute-checkmark"
                                    />
                                    <icon-cross-white
                                      v-else
                                      class="attribute-cross"
                                    />
                                    {{ attribute.name }}
                                  </li>
                                </ul>
                              </div>
                            </template>
                          </div>
                        </div>
                      </modal>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section class="summary__step" v-if="steps[1].fulfilled || isCompleted">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $t('progress.step1') }}
              </div>
            </div>
            <div class="col-sm-8">
              <div class="step__value">
                <div class="wrapper" v-if="dates[0]">
                  <span class="label">{{ $t('generic.labels.arrival') }}: </span>
                  <span class="value">{{ getFormattedDate(dates[0]) }}</span>
                </div>
                <div class="wrapper" v-if="dates[1]">
                  <span class="label">{{ $t('generic.labels.departure') }}: </span>
                  <span class="value">{{ getFormattedDate(dates[1]) }}</span>
                </div>
                <div class="wrapper" v-if="dates[0] && dates[1]">
                  <span class="label">{{ $t('generic.labels.nights') }}: </span>
                  <span class="value">{{ numNights }}</span>
                </div>

                <div class="wrapper margin-top">
                  <div class="wrapper" v-for="(person, index) of getGuestData" :key="index">
                    <span class="value">{{ person.amount }}x {{ person.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="step__edit" v-if="!isCompleted" @click="goToStep(1)">
            <iconEditGrey class="icon"></iconEditGrey>
          </div>
        </section>

        <section class="summary__step"
                 v-if="price.cancellation_terms && price.cancellation_terms.length > 0">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $t('progress.step2-ct') }}
              </div>
            </div>

            <div class="col-sm-8">
              <div v-for="(term, index) in price.cancellation_terms" :key="index">
                {{ term }}
              </div>
            </div>
          </div>

        </section>

        <section class="summary__step" v-if="getUserCampsites">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $t('progress.step2') }}
              </div>
            </div>
            <div class="col-sm-8">
              <div class="step__value step__value--margin-bottom"
                   v-for="(value, key) in getUserCampsites" :key="`usercampsite-${key}`">
                <div class="value" v-if="Object.keys(getUserCampsites).length > 1">
                  {{ key }}
                  <small>({{ $t('generic.labels.nights') }}: {{ value[0].days }})</small>
                </div>
                <div class="wrapper d-flex justify-content-between"
                     :class="{ 'no-padding' : isCompleted }" v-for="(campsite, index) in value"
                     :key="`ìnner-usercampsite-${index}`">
                  <span><!-- {{ campsite.name }} -->{{ campsite.description }}</span>
                  <span class="prices">{{
                      campsite.price | currency(campground.currency, language.code)
                    }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="step__edit" v-if="!isCompleted" @click="goToStep(2)">
            <iconEditGrey class="icon"></iconEditGrey>
          </div>
        </section>
        <section class="summary__step summary__step--border-bottom" v-if="getGuestBySeason">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $t('pages.bookingSummary.priceDetailsHeadline') }}
              </div>
            </div>
            <div class="col-sm-8">
              <div class="step__value step__value--margin-bottom"
                   v-for="(value, key) in getGuestBySeason" :key="key">
                <div class="value" v-if="Object.keys(getGuestBySeason).length > 1">
                  {{ key }}
                  <small>({{ $t('generic.labels.nights') }}: {{ value[0].days }})</small>
                </div>
                <div class="wrapper d-flex justify-content-between"
                     :class="{ 'no-padding' : isCompleted }" v-for="(person, index) in value"
                     :key="index">
                  <span><!--{{ person.amount }}x{{ person.name }} --> {{
                      person.description
                    }}</span>
                  <div class="prices">
                    <span class="price__value label" v-if="!person.included">{{
                        person.price | currency(campground.currency, language.code)
                      }}</span>
                    <span class="price__value label"
                          v-if="person.included">{{ $t('generic.labels.included') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="step__edit" v-if="!isCompleted" @click="goToStep(1)">
            <iconEditGrey class="icon"></iconEditGrey>
          </div>
        </section>

        <!-- subtotal start -->
        <section class="summary__sum d-flex justify-content-between align-items-center">
          <div class="sum-label">
            {{ $t('generic.labels.subtotal') }}
            <CiTooltip class="tooltip">
              <template slot="title">{{ $t('tooltips.subtotal.title') }}</template>
              <template slot="content">
                <p v-html="$t('tooltips.subtotal.content')"></p>
              </template>
            </CiTooltip>
          </div>
          <div class="sum-value">
            {{ price.subtotal | currency(campground.currency, language.code) }}
          </div>
        </section>
        <!-- subtotal end -->

        <section class="summary__step" v-if="getUserExtras && getUserExtras.length > 0">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $t('progress.step3') }}
              </div>
            </div>
            <div class="col-sm-8">
              <div class="step__value">
                <div class="wrapper d-flex justify-content-between"
                     :class="{ 'no-padding' : isCompleted }" v-for="(extra, index) in getUserExtras"
                     :key="extra.object_id+index">
                  <div class="label pr-3" v-html="extra.description"></div>
                  <div class="label" v-if="extra.price !== 0">
                    {{ extra.price | currency(campground.currency, language.code) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="step__edit" v-if="!isCompleted" @click="goToStep(3)">
            <iconEditGrey class="icon"></iconEditGrey>
          </div>
        </section>
        <section class="summary__step"
                 v-if="getMandatoryExtrasPriceNotZero && getMandatoryExtrasPriceNotZero.length > 0">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $tc('generic.labels.mandatory_extra', 0) }}
              </div>
            </div>
            <div class="col-sm-8">
              <div class="step__value">
                <div
                  class="wrapper no-padding d-flex justify-content-between"
                  :class="{ 'no-padding' : isCompleted }"
                  v-for="(mandatory, i) in getMandatoryExtrasPriceNotZero"
                  :key="`${i + '-' + mandatory.object_id}`"
                >
                  <span v-html="mandatory.description"></span>
                  <div class="label" v-if="mandatory.price !== 0">
                    {{ mandatory.price | currency(campground.currency, language.code) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section class="summary__step" v-if="steps[4].fulfilled || isCompleted">
          <div class="row">
            <div class="col-sm-4">
              <div class="step__label">
                {{ $t('progress.step4') }}
              </div>
            </div>
            <div class="col-sm-8">
              <div class="step__value">
                <div class="wrapper" v-if="billing.name && billing.lastname">
                  {{ billing.name }} {{ billing.lastname }}
                </div>
                <div class="wrapper" v-if="billing.street">
                  {{ billing.street }}
                </div>
                <div class="wrapper" v-if="billing.street_additional">
                  {{ billing.street_additional }}
                </div>
                <div class="wrapper" v-if="billing.zip && billing.city">
                  {{ billing.zip }} {{ billing.city }}
                </div>
                <div class="wrapper" v-if="billing.country">
                  {{ billing.country.label }}
                </div>
                <div class="wrapper margin-top" v-if="billing.phone">
                  {{ billing.phone }}
                </div>
                <div class="wrapper" v-if="billing.mail">
                  {{ billing.mail }}
                </div>
              </div>
            </div>

            <div class="col-sm-4" v-if="Object.keys(travellers).length > 0">
              <div class="step__label step__label--top">
                {{ $t('generic.labels.travellers') }}
              </div>
            </div>
            <div class="col-sm-8" v-if="Object.keys(travellers).length > 0">
              <div class="step__value step__value--inter">
                <div class="wrapper margin-top" v-for="(traveller, index) in travellers"
                     :key="index">
                  <div>{{ traveller.first_name }} {{ traveller.last_name }}</div>
                  <span>{{ getFormattedDate(traveller.birthdate) }}</span>
                </div>
              </div>
            </div>

            <div class="col-sm-4" v-if="car.license_plate || car.length || car.width">
              <div class="step__label step__label--top">
                {{ $t('generic.labels.car') }}
              </div>
            </div>
            <div class="col-sm-8" v-if="car.license_plate || car.length || car.width">
              <div class="step__value step__value--inter">
                <div class="wrapper">
                  <div v-if="car.license_plate">{{ car.license_plate }}</div>
                  <div v-if="car.length">{{ car.length }} {{ $t('generic.labels.length') }}</div>
                  <div v-if="car.width">{{ car.width }} {{ $t('generic.labels.width') }}</div>
                </div>
              </div>
            </div>

            <div class="col-sm-4" v-if="billing.message">
              <div class="step__label step__label--top">
                {{ $t('generic.labels.message_to_campground') }}
              </div>
            </div>
            <div class="col-sm-8" v-if="billing.message">
              <div class="step__value step__value--inter">
                {{ billing.message }}
              </div>
            </div>
          </div>
          <div class="step__edit" v-if="!isCompleted" @click="goToStep(4)">
            <iconEditGrey class="icon"></iconEditGrey>
          </div>
        </section>
      </div>
    </div>
    <footer class="summary__footer">
      <div class="container-fluid">
        <div class="footer">
          <div class="d-none d-lg-block sum__container">
            <CiSum
              :campgroundCurrency="campground.currency"
              :isCompleted="activeStep > 5"
              :language="language"
              :price="price"
              :userCurrency="currency">
            </CiSum>
          </div>
          <div class="summary__warning"
               v-if="getMandatoryExtrasPriceZero && getMandatoryExtrasPriceZero.length > 0">
            <div class="warning-title">
              <iconWarning class="icon"></iconWarning>
              {{ $t('pages.checkoutStepThree.infoBox.copy') }}
            </div>
            <div class="warning-content">
              <div v-for="mandatory in getMandatoryExtrasPriceZero" :key="mandatory.object_id">
                <div class="label" v-html="mandatory.description"></div>
              </div>
            </div>
          </div>
          <div class="summary__navigation">
            <CiGenericNavigation
              :ciClassesSecondary="activeStep === 4 && steps[4].fulfilled ? 'button--tertiary-outline button--no-border order-2' : 'button--tertiary-outline'"
              :ciActivePrimary="activeStep === 4 && steps[4].fulfilled"
              @clickPrimary="next()"
              @clickSecondary="toggleBookingDetails">
              <template slot="secondary">
                <iconArrowBlack class="icon icon--desktop icon--animated"
                                v-if="activeStep !== 4 || !steps[4].fulfilled"></iconArrowBlack>
                <span v-if="activeStep !== 4 || !steps[4].fulfilled">{{
                    $t('generic.buttons.hideDetails')
                  }}</span>
                <span v-if="activeStep === 4 && steps[4].fulfilled">{{
                    $t('generic.buttons.abort')
                  }}</span>
                <iconArrowBlack class="icon icon--mobile icon--animated"
                                v-if="activeStep !== 4 || !steps[4].fulfilled"></iconArrowBlack>
              </template>
              <template slot="primary">
                <span>{{ $t('generic.buttons.continueWithPayment') }}</span>
                <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
              </template>
            </CiGenericNavigation>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import {CiButton, CiGenericNavigation, CiSum, CiTooltip} from '@/components'
import bookingDataMixin from '@/mixins/bookingDataMixin'
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import {mapGetters, mapActions} from 'vuex'

import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconClose from '@/assets/icons/close-dark.svg'
import iconCloseDark from '@/assets/icons/close-dark.svg'
import iconEditGrey from '@/assets/icons/edit-grey.svg'
import iconInfoBlack from '@/assets/icons/info-black.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'
import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconCrossWhite from '@/assets/icons/cross-white.svg'

export default {
  name: 'BookingSummary',
  mixins: [bookingDataMixin, bookingPriceMixin],
  components: {
    CiButton,
    CiGenericNavigation,
    CiSum,
    CiTooltip,
    iconArrowBlack,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconCrossWhite,
    iconClose,
    iconCloseDark,
    iconEditGrey,
    iconInfoBlack,
    iconWarning,
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      showBookingDetails: 'showBookingDetails',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      billing: 'billing',
      car: 'car',
      confirmation: 'confirmation',
      dates: 'dates',
      isCompleted: 'isCompleted',
      price: 'price',
      selectedPersons: 'selectedPersons',
      travellers: 'travellers',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
      urls: 'urls',
      attributes: 'attributes',
    }),

    getGuestData() {
      return this.isCompleted
        ? this.confirmation.booking_data.campsites[0].persons
        : this.selectedPersons.map(guest => {
          const p = this.campground.persons.find(p => p.id === guest.id)
          return {...p, ...guest}
        })
    },
  },
  methods: {
    ...mapActions('application', {
      toggleBookingDetails: 'toggleBookingDetails',
    }),

    goToStep(step) {
      this.toggleBookingDetails()
      this.$router.push({
        name: 'Step',
        params: {...this.$route.params, step: step},
      })
    },

    next() {
      this.toggleBookingDetails()
      this.$router.push({
        name: 'Step',
        params: {...this.$route.params, step: this.activeStep + 1},
      })
    },

    onShowBookingDetails(value) {
      /* istanbul ignore else */
      if (value) {
        this.$refs.scrollContainer.scrollTop = 0
      }
    },
  },
  watch: {
    showBookingDetails: {
      handler: 'onShowBookingDetails',
      immediate: false,
    },
  },
}
</script>

<style scoped lang="scss">
.no-touch .icon-close {
  display: none;
}

.summary {
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0 auto;
  min-height: 0;
  padding-bottom: 0;
  position: relative;
  justify-content: space-between;

  @include media-breakpoint-up(sm) {
    padding-bottom: 10px;
  }

  @include media-breakpoint-up(lg) {
    padding: 0;
  }
}

.scroll-container {
  width: 100%;
  height: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @include media-breakpoint-up(lg) {
    height: 100vh;
  }

  > .container-fluid {
    /* max-width: 670px; */
    max-width: 88%;
  }
}

.summary__header {
  position: relative;
  margin: 20px 0;

  @include media-breakpoint-up(md) {
    margin: 60px 0 50px;
  }

  .header__headline {
    @include font-format(1.375rem, 1.313em);

    margin: 0 auto 30px;
    max-width: 280px;
    text-align: center;

    @include media-breakpoint-up(md) {
      margin: 0 0 50px;
      max-width: none;
    }
  }

  .header__close {
    @include font-format(0.625rem, 0.625rem);

    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    opacity: 0.6;
    position: absolute;
    right: 0;
    top: 0;

    .close-label {
      margin-top: 10px;
    }
  }

  .header__image-wrapper {
    padding: 20px;
  }

  .header__image {
    border-radius: 50%;
    border: 5px solid white;
    box-shadow: 0 0 0 6px rgba(0, 0, 0, 0.01), 0 20px 48px 0 rgba(53, 53, 53, 0.17);
    margin: 0;
    overflow: hidden;

    img {
      width: 100%;
    }
  }

  .header__info {
    display: flex;
    align-items: center;
    height: 100%;

    .address-list {
      @include font-format(0.875rem, 1.25rem);

      list-style: none;
      margin: 0;
      padding: 0;
    }
  }
}

.summary__footer {
  background: white;
  border-top: 1px solid #979797;
  position: relative;
  width: 100%;
  padding: 0 0 80px 0;

  @include media-breakpoint-up(lg) {
    padding: 0;
  }

  > .container-fluid {
    max-width: 670px;
  }

  .footer {
    .sum {
      margin-top: 10px;
    }
  }
}

.summary__step {
  @include font('gotham_bookregular');
  @include font-format(0.875rem, 1.125rem);

  border-top: 1px solid #979797;
  padding: 40px 25px 25px;
  position: relative;

  .step__label {
    @include font('gotham_mediumregular');
    @include font-format(0.875rem, 1.125rem);

    margin: 0 0 20px 0;

    &.step__label--inter {
      @include font('gotham_bookregular');

      margin: 0;
    }

    &.step__label--top {
      margin-top: 20px;
    }
  }

  .step__value {
    &.step__value--inter {
      margin-top: 20px;
    }

    &.step__value--margin-bottom {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .wrapper {
      padding-right: 0;

      &.margin-top {
        margin-top: 20px;
      }

      &.no-padding {
        padding: 0;
      }

      span:not(.label):first-child {
        padding-right: 15px;
      }
    }

    .value {
      @include font('gotham_mediumregular');
      @include font-format(0.875rem, 1.125rem);

      small {
        @include font('gotham_bookregular');
      }
    }

    .prices {
      display: block;
    }

    .season__label {
      margin-bottom: 10px;
    }
  }

  .step__edit {
    position: absolute;
    top: 5px;
    right: 0;
    cursor: pointer;
  }

  &.summary__step--border-bottom {
    border-bottom: 1px solid #979797;
  }

  &.summary__step--no-border {
    border: 0;
  }
}

.summary__sum {
  background-color: rgba(155, 155, 155, 0.1);
  height: 50px;
  margin: 10px 0;

  .sum-label {
    display: flex;
    align-items: center;
    margin-left: 15px;
    height: 100%;
  }

  .sum-value {
    margin-right: 15px;
  }
}

.summary__warning {
  @include font('gotham_bookregular');
  @include font-format(0.75rem, 1.25rem);

  background-color: #fefcdd;
  border: 1px solid #f8e71c;
  margin: 15px 0 0 0;

  .warning-title {
    position: relative;
    background-color: #f8e71c66;
    padding: 10px 25px 10px 60px;

    .icon {
      position: absolute;
      left: 20px;
      top: 10px;
      width: 20px;
      height: auto;
    }
  }

  .warning-content {
    padding: 10px 25px 10px 60px;
  }
}

.summary__navigation {
  margin: 20px 0;

  .button--tertiary-outline {
    .icon--mobile {
      display: block;
      transform: translate3d(0, -50%, 0) rotate(-90deg);

      @include media-breakpoint-up(md) {
        display: none;
      }
    }

    .no-touch &:hover span + .icon--mobile {
      transform: translate3d(0, -50%, 0) rotate(-90deg);
    }

    .icon--desktop {
      display: none;

      @include media-breakpoint-up(md) {
        display: block;
        transform: translate3d(0, -50%, 0) rotate(-90deg);
      }

      @include media-breakpoint-up(lg) {
        display: block;
        transform: translate3d(0, -50%, 0) rotate(0);
      }
    }

    .no-touch &:hover .icon--desktop.icon--animated {
      @include media-breakpoint-up(sm) {
        transform: translate3d(0, -50%, 0) rotate(-90deg);
      }

      @include media-breakpoint-up(lg) {
        transform: translate3d(-5px, -50%, 0);
      }
    }
  }
}
</style>
